import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiClock, FiCheck, FiX, FiChevronLeft } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

function ClockTimePicker({
  selectedTime = '',
  onChange,
  placeholder = 'Select tee time',
  disabled = false,
  minTime = '00:00',
  maxTime = '23:55',
  className = '',
  enableHaptics = true
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState('hour'); // 'hour' or 'minute'
  const [tempHour, setTempHour] = useState('');
  const [tempMinute, setTempMinute] = useState('');
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // Initialize temp values when picker opens
  useEffect(() => {
    if (isOpen) {
      if (selectedTime) {
        const [hours, minutes] = selectedTime.split(':').map(Number);
        setTempHour(hours);
        setTempMinute(minutes);
        setCurrentStep('hour');
      } else {
        setTempHour('');
        setTempMinute('');
        setCurrentStep('hour');
      }
    }
  }, [isOpen, selectedTime]);

  // Haptic feedback function
  const triggerHaptic = (type = 'light') => {
    if (!enableHaptics) return;
    
    try {
      if ('vibrate' in navigator) {
        switch (type) {
          case 'light':
            navigator.vibrate(10);
            break;
          case 'medium':
            navigator.vibrate(20);
            break;
          default:
            navigator.vibrate(10);
        }
      }
    } catch (error) {
      // Haptic feedback not supported
    }
  };

  // Generate hour options (24-hour format)
  const generateHourOptions = () => {
    const options = [];
    const startHour = parseInt(minTime.split(':')[0]);
    const endHour = parseInt(maxTime.split(':')[0]);
    
    for (let hour = startHour; hour <= endHour; hour++) {
      const displayHour = hour.toString().padStart(2, '0');
      
      options.push({
        value: hour,
        label: displayHour,
        display: displayHour
      });
    }
    
    return options;
  };

  // Generate minute options (5-minute increments)
  const generateMinuteOptions = () => {
    const options = [];
    
    for (let minute = 0; minute < 60; minute += 5) {
      const displayMinute = minute.toString().padStart(2, '0');
      
      options.push({
        value: minute,
        label: `:${displayMinute}`,
        display: displayMinute
      });
    }
    
    return options;
  };

  const formatDisplayTime = (timeValue) => {
    if (!timeValue) return placeholder;
    
    const [hours, minutes] = timeValue.split(':').map(Number);
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
  };

  const handleHourSelect = (hour) => {
    setTempHour(hour);
    setCurrentStep('minute');
    triggerHaptic('medium');
  };

  const handleMinuteSelect = (minute) => {
    setTempMinute(minute);
    triggerHaptic('medium');
  };

  const handleBackToHour = () => {
    setCurrentStep('hour');
    triggerHaptic('light');
  };

  const handleConfirm = () => {
    if (tempHour !== '' && tempMinute !== '') {
      const timeString = `${tempHour.toString().padStart(2, '0')}:${tempMinute.toString().padStart(2, '0')}`;
      onChange(timeString);
      setIsOpen(false);
      triggerHaptic('medium');
    }
  };

  const handleCancel = () => {
    if (selectedTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      setTempHour(hours);
      setTempMinute(minutes);
    } else {
      setTempHour('');
      setTempMinute('');
    }
    setCurrentStep('hour');
    setIsOpen(false);
    triggerHaptic('light');
  };

  const handleClear = () => {
    setTempHour('');
    setTempMinute('');
    setCurrentStep('hour');
  };

  const isConfirmDisabled = tempHour === '' || tempMinute === '';

  const hourOptions = generateHourOptions();
  const minuteOptions = generateMinuteOptions();

  return (
    <div className={`relative ${className}`}>
      {/* Trigger Button */}
      <motion.button
        type="button"
        whileHover={{ scale: disabled ? 1 : 1.02 }}
        whileTap={{ scale: disabled ? 1 : 0.98 }}
        onClick={() => !disabled && setIsOpen(true)}
        disabled={disabled}
        className={`
          w-full px-4 py-3 border border-border-subtle rounded-xl text-left
          flex items-center justify-between transition-all duration-200
          min-h-[52px] font-medium
          ${disabled 
            ? 'bg-surface text-text-muted cursor-not-allowed' 
            : 'bg-surface focus:bg-card hover:border-accent hover: focus:ring-2 focus:ring-accent focus:border-accent'
          }
          ${isOpen ? 'border-accent  bg-card' : ''}
        `}
      >
        <span className={selectedTime ? 'text-text-primary' : 'text-text-muted'}>
          {formatDisplayTime(selectedTime)}
        </span>
        <SafeIcon icon={FiClock} className="w-5 h-5 text-text-muted" />
      </motion.button>

      {/* Custom Time Picker Modal */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 bg-black bg-opacity-50 z-[60] ${
              isMobile 
                ? 'flex items-end pb-16' 
                : 'flex items-center justify-center p-4'
            }`}
            onClick={handleCancel}
          >
            <motion.div
              initial={{ opacity: 0, y: isMobile ? 100 : 0, scale: isMobile ? 1 : 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: isMobile ? 100 : 0, scale: isMobile ? 1 : 0.95 }}
              transition={{ duration: 0.2 }}
              className={`w-full bg-card shadow-modal overflow-hidden ${
                isMobile
                  ? 'rounded-t-xl max-h-[calc(100vh-4rem)]'
                  : 'rounded-xl max-w-lg max-h-[80vh] mx-auto'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="flex items-center justify-between px-6 py-4 border-b border-border-subtle">
                <div className="flex items-center space-x-3">
                  {currentStep === 'minute' && (
                    <button
                      type="button"
                      onClick={handleBackToHour}
                      className="w-8 h-8 rounded-lg bg-surface hover:bg-card flex items-center justify-center transition-colors duration-200"
                    >
                      <SafeIcon icon={FiChevronLeft} className="w-4 h-4 text-text-secondary" />
                    </button>
                  )}
                  <h3 className="text-lg font-bold text-text-primary">
                    {currentStep === 'hour' ? 'Select Hour' : 'Select Minutes'}
                  </h3>
                </div>
                <div className="flex items-center space-x-2">
                  {(tempHour !== '' || tempMinute !== '') && (
                    <button
                      type="button"
                      onClick={handleClear}
                      className="px-3 py-1.5 text-sm text-text-secondary hover:text-text-primary hover:bg-surface rounded-md transition-colors duration-200"
                    >
                      Clear
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={handleCancel}
                    className="w-8 h-8 bg-surface rounded-lg flex items-center justify-center hover:bg-card transition-colors duration-200"
                    aria-label="Close time picker"
                  >
                    <SafeIcon icon={FiX} className="w-4 h-4 text-text-secondary" />
                  </button>
                </div>
              </div>

              {/* Current Selection Display */}
              {(tempHour !== '' || tempMinute !== '') && (
                <div className="px-6 py-3 bg-accent/5 border-b border-accent/20">
                  <div className="flex items-center justify-center">
                    <span className="text-lg font-bold text-accent">
                      {tempHour !== '' && tempMinute !== '' ? (
                        formatDisplayTime(`${tempHour.toString().padStart(2, '0')}:${tempMinute.toString().padStart(2, '0')}`)
                      ) : tempHour !== '' ? (
                        `${tempHour.toString().padStart(2, '0')}:--`
                      ) : (
                        'Select time'
                      )}
                    </span>
                  </div>
                </div>
              )}

              {/* Hour Selection - Digital Grid */}
              {currentStep === 'hour' && (
                <div className="p-4">
                  <div className="mb-4">
                    <h4 className="text-center text-sm font-medium text-text-secondary mb-2">Select Hour (24-hour format)</h4>
                    <div className="text-center">
                      <span className="text-2xl font-mono font-bold text-text-primary">
                        {tempHour !== '' ? tempHour.toString().padStart(2, '0') : '--'}:--
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                    {hourOptions.map((option) => (
                      <motion.button
                        type="button"
                        key={option.value}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleHourSelect(option.value)}
                        className={`
                          p-3 rounded-xl text-center transition-all duration-200
                          min-h-[48px] font-mono font-bold text-lg
                          ${tempHour === option.value
                            ? 'bg-accent text-white'
                            : 'bg-surface text-text-secondary hover:bg-card border border-border-subtle'
                          }
                        `}
                      >
                        {option.display}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Minute Selection */}
              {currentStep === 'minute' && (
                <div className="p-4">
                  <div className="mb-4">
                    <h4 className="text-center text-sm font-medium text-text-secondary mb-2">Select Minutes</h4>
                    <div className="text-center">
                      <span className="text-2xl font-mono font-bold text-text-primary">
                        {tempHour !== '' ? tempHour.toString().padStart(2, '0') : '--'}:{tempMinute !== '' ? tempMinute.toString().padStart(2, '0') : '--'}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                    {minuteOptions.map((option) => (
                      <motion.button
                        type="button"
                        key={option.value}
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        onClick={() => handleMinuteSelect(option.value)}
                        className={`
                          p-3 rounded-xl text-center transition-all duration-200
                          min-h-[48px] font-mono font-bold text-lg
                          ${tempMinute === option.value
                            ? 'bg-accent text-white'
                            : 'bg-surface text-text-secondary hover:bg-card border border-border-subtle'
                          }
                        `}
                      >
                        {option.display}
                      </motion.button>
                    ))}
                  </div>
                </div>
              )}

              {/* Footer */}
              <div className={`px-6 py-4 border-t border-border-subtle flex space-x-3 ${
                isMobile ? 'pb-8' : ''
              }`}>
                <motion.button
                  type="button"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleCancel}
                  className="flex-1 px-4 py-3 border border-border-subtle text-text-secondary rounded-md hover:bg-surface transition-all duration-200 font-medium min-h-[48px]"
                >
                  Cancel
                </motion.button>
                <motion.button
                  type="button"
                  whileHover={{ scale: isConfirmDisabled ? 1 : 1.02 }}
                  whileTap={{ scale: isConfirmDisabled ? 1 : 0.98 }}
                  onClick={handleConfirm}
                  disabled={isConfirmDisabled}
                  className="flex-1 px-4 py-3 bg-accent text-white rounded-md hover:bg-interactive-hover disabled:bg-subtle disabled:text-text-muted transition-all duration-200 font-medium min-h-[48px] flex items-center justify-center"
                >
                  <SafeIcon icon={FiCheck} className="w-4 h-4 mr-2" />
                  Confirm
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default ClockTimePicker;